import React, { useCallback, useContext, useMemo, useState } from "react";
import "./resultsTable.css";
//import PageHeader from "../layouts/pageHeader/pageHeader";
import Pagination from "./pagination";
import Operations from "./operations";
import NoResults from "./noResults";
import CrfDocLink from "../CrfDocLink/crfDocLink";
import CdeDetailsLink from "../CdeDetailsLink/cdeDetailsLink";
import HelpIcon from "../HelpIcon/helpIcon";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Link } from "react-router-dom";
import FormBuilderCartContext from "../../FormBuilder/cart/formBuilderCartContext";

const getCrfKey = r => {
  return `${r.crfId}_${r.diseaseId}_${r.subDiseaseId}_${r.domainId}_${r.subDomainId}`;
}

const getCdeKey = r => {
  return `${r.cdeId}_${r.crfId}_${r.diseaseId}_${r.subDiseaseId}`;
}

export default function ResultsTable({ data, reportType, defaultSorting, notificationsContext }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sorting, setSorting] = useState(defaultSorting || { column: 'crfName', direction: 'asc' });

  const { newCartItems } = useContext(FormBuilderCartContext);

  const getKey = useCallback((r) => {
    switch (reportType) {
      case 'CRF':
        return getCrfKey(r);
      case 'CDE':
      case 'CDE_CART':
        return getCdeKey(r);
      default:
        break;
    }
  }, [reportType]);

  const resetSelectedRows = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const handleCheckboxChange = useCallback((id) => {
    if (id === "all") {
      setSelectedRows((prevSelectedRows) =>
        prevSelectedRows.length === data.length
          ? []
          : data.map((item) => ({
            id: getKey(item),
            crfId: item.crfId,
            diseaseId: item.diseaseId,
            ...(reportType === "CRF" && {
              externalUrl: item.externalUrl,
              crfExtension: item.crfExtension,
              cdeIds: item.cdeIds,
              crfName: item.crfName
            }),
            ...(reportType.startsWith('CDE') && {
              cdeId: item.cdeId
            }),
          }))
      );
    } else {
      setSelectedRows((prevSelectedRows) => {
        const row = data.find((item) =>
          getKey(item) === id
        );

        return prevSelectedRows.some((row) => row.id === id)
          ? prevSelectedRows.filter((row) => row.id !== id)
          : [
            ...prevSelectedRows,
            {
              id,
              crfId: row.crfId,
              diseaseId: row.diseaseId,
              ...(reportType === "CRF" && {
                externalUrl: row.externalUrl,
                crfExtension: row.crfExtension,
                cdeIds: row.cdeIds,
                crfName: row.crfName
              }),
              ...(reportType.startsWith('CDE') && {
                cdeId: row.cdeId
              }),
            },
          ]
      }
      );
    }
  }, [data, getKey, reportType]);

  const operations = useMemo(() => {
    switch (reportType) {
      case 'CRF':
        return [
          { id: 'choose', name: "- Choose an operation -", },
          { id: 'fb_add', name: "Add Selected to Form Builder", },
          { id: 'csv', name: "Download Table Contents into Zip File", },
        ];

      case 'CDE':
        return [
          { id: 'choose', name: "- Choose an operation -", },
          { id: 'cde', name: "CDE Detailed Report", },
          { id: 'fb_add', name: "Add Selected to Form Builder", },
        ];

      case 'CDE_CART':
        return [
          { id: 'choose', name: '- Choose an operation -', },
          { id: 'fb_undo', name: 'Undo Add', },
          { id: 'fb_remove', name: 'Remove Selected from Cart', },
          { id: 'fb_clear', name: 'Clear Cart', },
          { id: 'fb_csv', name: 'Data Dictionary for Export', },
        ];

      default:
        break;
    }
  }, [reportType]);

  const columns = useMemo(() => {
    switch (reportType) {
      case 'CRF':
        return [
          { id: 'crfName', title: "CRF Module / Guidance", helpText: 'A case report form to be used in a study and is comprised of recommended CDEs for a specific disorder. It can be used as a template and/or modified as needed for a study.' },
          { id: 'crfDescription', title: "Description", helpText: 'Description of the data captured by the CDE/measure, including any limits on when the CDE is appropriate.' },
          { id: 'associatedCde', title: "CDEs", helpText: 'Common Data Element – Information that describes a piece of data to be collected in a study and is common to multiple datasets or used across different studies.' },
          { id: 'version', title: "Version #", },
          { id: 'versionDate', title: "Version Date", },
          { id: 'diseaseName', title: "Disease Name", helpText: 'Name of disorder with which the CDE is associated.' },
          { id: 'subDiseaseName', title: "Subdisease Name", },
        ];

      case 'CDE':
        return [
          { id: 'cdeId', title: "CDE ID", helpText: 'Serial number of the CDE in the database.' },
          { id: 'cdeName', title: "CDE Name", },
          { id: 'definition', title: "Definition", helpText: 'Description of the data captured by the CDE/measure, including any limits on when the CDE is appropriate.' },
          { id: 'classification', title: "Classification", helpText: 'Level of importance for a CDE/measure as it relates to usage in clinical research studies, assigned by subject matter experts.' },
          { id: 'crfName', title: "CRF Name", helpText: 'Name of the CRF or Instrument on which the CDE appears.' },
          { id: 'copyright', title: "Copyrighted or trademarked", helpText: 'Indicates whether the CDE is from a copyrighted or trademarked CRF or Instrument.' },
          { id: 'diseaseName', title: "Disease Name", helpText: 'Name of disorder with which the CDE is associated.' },
          { id: 'subDiseaseName', title: "Subdisease Name", },
        ];

      case 'CDE_CART':
        return [
          { id: 'cdeId', title: 'CDE ID' },
          { id: 'cdeName', title: 'CDE Name' },
          { id: 'definition', title: 'Definition' },
          { id: 'crfName', title: 'CRF Name' },
          { id: 'copyright', title: 'Copyright or Trademark' },
        ];

      default:
        break;
    }
  }, [reportType]);

  const pageHeaderDesc = useMemo(() => {
    switch (reportType) {
      case 'CRF':
        return "Download results by clicking the checkboxes on the far right side of the table. Select the header checkbox to select all CRFs listed on the first page. Then choose 'Download Table Contents into Zip File' from the Choose an operation dropdown menu.";

      case 'CDE':
        return "The search results below can be downloaded by clicking the checkboxes on the far right side of the table. Select the header checkbox to select all CDEs listed on the first page. Then choose CDE Detailed Report from the Choose an operation dropdown menu.";

      case 'CDE_CART':
        return '';

      default:
        break;
    }
  }, [reportType]);

  const totalPages = useMemo(() => Math.ceil(data.length / pageSize), [data, pageSize]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  }, []);

  const handleSortChange = useCallback((column) => {
    setSorting(prevSorting => {
      const newSorting = { column, direction: 'asc' };

      if (prevSorting.column === column)
        newSorting.direction = prevSorting.direction === 'asc' ? 'desc' : 'asc';

      return newSorting;
    });
  }, []);

  const sortedData = useMemo(() => {
    return data.toSorted((a, b) => {
      if (sorting.direction === 'asc')
        return ('' + a[sorting.column]).trim().localeCompare(('' + b[sorting.column]).trim());
      else
        return ('' + b[sorting.column]).trim().localeCompare(('' + a[sorting.column]).trim());
    })
  }, [data, sorting]);

  const paginatedData = useMemo(() => sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize), [sortedData, currentPage, pageSize]);

  const tableData = useCallback(() => {
    if (reportType === "CRF") {
      return paginatedData.map((r) => (
        <tr key={getKey(r)}>
          <td>
            <input
              type="checkbox"
              checked={selectedRows.some((row) => row.id === getKey(r))}
              onChange={() => handleCheckboxChange(getKey(r))}
            />
          </td>
          <td>
            <CrfDocLink
              crfExtension={r.crfExtension}
              crfId={r.crfId}
              crfName={r.crfName}
              externalUrl={r.externalUrl}
            />
          </td>
          <td>
            {r.crfDescription ? r.crfDescription : "No description available"}
          </td>
          <td>
            <CdeDetailsLink
              associatedCDE={r.associatedCDE}
              crfId={r.crfId}
              crfName={r.crfName}
              domainName={r.domainName}
              reportType={reportType}
              subDomainName={r.subDomainName}
              diseaseId={r.diseaseId}
            />
          </td>
          <td>{r.version}</td>
          <td>{r.versionDate}</td>
          <td>{r.diseaseName}</td>
          <td>{r.subDiseaseName}</td>
        </tr>
      ));
    } else if (reportType === 'CDE') {
      return paginatedData.map((r) => (
        <tr key={getKey(r)}>
          <td>
            <input
              type="checkbox"
              checked={selectedRows.some(
                (row) => row.id === getKey(r)
              )}
              onChange={() => handleCheckboxChange(getKey(r))}
            />
          </td>
          <td>{r.cdeId}</td>
          <td>{r.cdeName}</td>
          <td>{r.definition}</td>
          <td>{r.classification}</td>
          <td>{r.crfName}</td>
          <td>{r.copyright ? "True" : "False"}</td>
          <td>{r.diseaseName}</td>
          <td>{r.subDiseaseName}</td>
        </tr>
      ));
    } else if (reportType === 'CDE_CART') {
      return paginatedData.map((r) => (
        <tr key={getKey(r)}>
          <td>
            <input
              type="checkbox"
              checked={selectedRows.some(
                (row) => row.id === getKey(r)
              )}
              onChange={() => handleCheckboxChange(getKey(r))}
            />
          </td>
          <td>{r.cdeId}</td>
          <td>
            <Link to={`/cde-details/CDE/${r.diseaseId}/${r.cdeId}`} target="_blank">{r.cdeName}</Link>
            {newCartItems.some(i => i === `${r.cdeId}_${r.crfId}_${r.diseaseId}`) && <div className="fb-new-to-cart">NEW TO CART!</div>}
          </td>
          <td>{r.definition}</td>
          <td>{r.crfName}</td>
          <td>{r.copyright ? "True" : "False"}</td>
        </tr>
      ));
    }
  }, [getKey, handleCheckboxChange, newCartItems, paginatedData, reportType, selectedRows]);

  return (
    <>
      {data.length > 0 && (
        <>
          <Operations
            operations={operations}
            selectedRows={selectedRows}
            reportType={reportType}
            resetSelectedRows={resetSelectedRows}
            alwaysEnabled={reportType === 'CDE_CART'}
            notificationsContext={notificationsContext}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          <div className="results-table">
            <table>
              <caption>{pageHeaderDesc}</caption>
              <thead>
                <tr>
                  <th
                    className="selectAllHeader"
                    onClick={() => handleCheckboxChange("all")}
                  >
                    Select all
                  </th>
                  {columns.map((c) => (
                    <th key={c.id} className={c.id === sorting.column ? 'active' : null} onClick={() => handleSortChange(c.id)}>
                      {c.title}{" "}
                      {sorting.column === c.id && <FontAwesomeIcon icon={sorting.direction === 'asc' ? faChevronUp : faChevronDown} />}{" "}
                      {c.helpText && <HelpIcon id={c.id + '-tooltip'} content={c.helpText} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{tableData()}</tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
      {data.length === 0 && <NoResults />}
    </>
  );
}
